import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Alert,
  Chip, Card, CardContent, Grid, Tooltip, InputAdornment,
  TablePagination, TableSortLabel, Checkbox, FormControl, InputLabel,
  Select, Avatar, Stack, Autocomplete, SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon,
  Visibility as ViewIcon, Edit as EditIcon, Archive as ArchiveIcon,
  Pets as PetsIcon, FileDownload as ExportIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

// ── Interfaces ──────────────────────────────────────────────────────────────

interface Patient {
  id: string;
  name: string;
  species: string;
  species_id?: number;
  breed: string;
  status: string;
  client_id: string;
  client_name?: string;
  date_of_birth?: string;
  gender?: string;
  weight?: number;
  microchip_id?: string;
  last_visit?: string;
  created_at?: string;
}

interface Species {
  id: number;
  name: string;
}

interface Breed {
  id: number;
  name: string;
  species_id: number;
}

interface ClientOption {
  id: string;
  label: string;
}

const emptyForm = {
  name: '',
  species_id: '',
  breed_id: '',
  date_of_birth: '',
  gender: 'Unknown',
  weight: '',
  microchip_id: '',
  color: '',
  allergies: '',
  chronic_illnesses: '',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const getSpeciesEmoji = (species: string) => {
  const s = (species || '').toLowerCase();
  if (s.includes('dog') || s.includes('canine')) return '🐶';
  if (s.includes('cat') || s.includes('feline')) return '🐱';
  if (s.includes('bird') || s.includes('avian') || s.includes('parrot')) return '🦜';
  if (s.includes('rabbit')) return '🐰';
  if (s.includes('guinea') || s.includes('hamster')) return '🐹';
  if (s.includes('reptile') || s.includes('lizard') || s.includes('snake')) return '🦎';
  return '🐾';
};

const getSpeciesColor = (species: string): 'primary' | 'warning' | 'success' | 'secondary' | 'default' => {
  const s = (species || '').toLowerCase();
  if (s.includes('dog') || s.includes('canine')) return 'primary';
  if (s.includes('cat') || s.includes('feline')) return 'warning';
  if (s.includes('bird') || s.includes('avian') || s.includes('parrot')) return 'success';
  if (s.includes('rabbit') || s.includes('guinea') || s.includes('hamster')) return 'secondary';
  return 'default';
};

const getSexLabel = (gender?: string) => {
  if (!gender) return '—';
  const g = gender.toLowerCase();
  if (g.includes('neutered') || g.includes('castrated')) return '♂ Neutered';
  if (g.includes('spayed')) return '♀ Spayed';
  if (g === 'male' || (g.startsWith('male') && !g.includes('neutered'))) return '♂ Male';
  if (g === 'female' || (g.startsWith('female') && !g.includes('spayed'))) return '♀ Female';
  if (g === 'unknown' || g === '') return '—';
  return gender;
};

const formatAge = (dob?: string): string => {
  if (!dob) return '—';
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return '—';
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  if (now.getDate() < birth.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  if (years < 0) return '< 1mo';
  if (years === 0) return `${months}mo`;
  return months > 0 ? `${years}y ${months}mo` : `${years}y`;
};

const fmtDate = (d?: string | null): string => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const isOldVisit = (lastVisit?: string): boolean => {
  if (!lastVisit) return false;
  return Date.now() - new Date(lastVisit).getTime() > 365 * 24 * 60 * 60 * 1000;
};

const getStatusColor = (status: string): 'success' | 'default' | 'error' | 'warning' => {
  switch (status) {
    case 'active': return 'success';
    case 'deceased': return 'error';
    case 'inactive': case 'lost': return 'warning';
    default: return 'default';
  }
};

type SortField = 'name' | 'species' | 'breed' | 'gender' | 'age' | 'client_name' | 'last_visit' | 'status';
type SortDir = 'asc' | 'desc';

const exportToCSV = (rows: Patient[]) => {
  const headers = ['Name', 'Species', 'Breed', 'Sex', 'Age', 'DOB', 'Owner', 'Microchip', 'Last Visit', 'Status'];
  const data = rows.map(p => [
    p.name, p.species || '', p.breed || '', p.gender || '',
    formatAge(p.date_of_birth), p.date_of_birth || '',
    p.client_name || '', p.microchip_id || '',
    fmtDate(p.last_visit), p.status,
  ]);
  const csv = [headers, ...data]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'patients.csv'; a.click();
  URL.revokeObjectURL(url);
};

// ── Component ─────────────────────────────────────────────────────────────────

const PatientsPage = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);

  // Search & Filters
  const [search, setSearch] = useState('');
  const [filterSpecies, setFilterSpecies] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSex, setFilterSex] = useState('');

  // Sort
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Archive confirm
  const [archiveTarget, setArchiveTarget] = useState<Patient | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState('');

  // New patient dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [speciesList, setSpeciesList] = useState<Species[]>([]);
  const [breedList, setBreedList] = useState<Breed[]>([]);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);

  // ISSUE-004: Microchip uniqueness check
  const [microchipError, setMicrochipError] = useState<string | null>(null);
  const [checkingMicrochip, setCheckingMicrochip] = useState(false);

  useEffect(() => { fetchPatients(); }, []);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const res = await api.get(API_ENDPOINTS.PATIENTS.LIST);
      setPatients(res.data || res || []);
    } catch (err) {
      console.error('Failed to fetch patients:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [speciesRes, clientsRes] = await Promise.all([
        api.get(API_ENDPOINTS.PATIENTS.SPECIES),
        api.get(API_ENDPOINTS.CLIENTS.LIST),
      ]);
      setSpeciesList(speciesRes.data || speciesRes || []);
      const clients = clientsRes.data || clientsRes || [];
      setClientOptions(
        clients.map((c: { id: string; first_name: string; last_name: string; email: string }) => ({
          id: c.id,
          label: `${c.first_name} ${c.last_name} (${c.email})`,
        }))
      );
    } catch (err) {
      console.error('Failed to fetch reference data:', err);
    }
  };

  const fetchBreeds = async (speciesId: string) => {
    if (!speciesId) { setBreedList([]); return; }
    try {
      const res = await api.get(`${API_ENDPOINTS.PATIENTS.BREEDS}?species_id=${speciesId}`);
      setBreedList(res.data || res || []);
    } catch (err) {
      console.error('Failed to fetch breeds:', err);
      setBreedList([]);
    }
  };

  // ── Stats ─────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const total = patients.length;
    const active = patients.filter(p => p.status === 'active').length;
    const now = new Date();
    const newThisMonth = patients.filter(p => {
      if (!p.created_at) return false;
      const dt = new Date(p.created_at);
      return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
    }).length;
    const withDob = patients.filter(p => p.date_of_birth);
    let avgAge = '—';
    if (withDob.length > 0) {
      const sumMs = withDob.reduce((s, p) => s + (Date.now() - new Date(p.date_of_birth!).getTime()), 0);
      avgAge = (sumMs / withDob.length / (365.25 * 86400000)).toFixed(1) + ' yrs';
    }
    return { total, active, newThisMonth, avgAge };
  }, [patients]);

  // ── Unique species for filter dropdown ───────────────────────────────────

  const uniqueSpecies = useMemo(() => {
    const set = new Set(patients.map(p => p.species).filter(Boolean));
    return Array.from(set).sort();
  }, [patients]);

  // ── Filtered + sorted patients ────────────────────────────────────────────

  const filtered = useMemo(() => {
    let result = patients;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.species || '').toLowerCase().includes(q) ||
        (p.breed || '').toLowerCase().includes(q) ||
        (p.client_name || '').toLowerCase().includes(q) ||
        (p.microchip_id || '').toLowerCase().includes(q)
      );
    }

    if (filterSpecies.length > 0) {
      result = result.filter(p => filterSpecies.includes(p.species));
    }
    if (filterStatus) {
      result = result.filter(p => p.status === filterStatus);
    }
    if (filterSex) {
      result = result.filter(p => {
        const g = (p.gender || '').toLowerCase();
        if (filterSex === 'male') return g === 'male' || (g.startsWith('male') && !g.includes('neutered'));
        if (filterSex === 'female') return g === 'female' || (g.startsWith('female') && !g.includes('spayed'));
        if (filterSex === 'neutered') return g.includes('neutered') || g.includes('castrated');
        if (filterSex === 'spayed') return g.includes('spayed');
        return true;
      });
    }

    return [...result].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'age') {
        const aTime = a.date_of_birth ? new Date(a.date_of_birth).getTime() : 0;
        const bTime = b.date_of_birth ? new Date(b.date_of_birth).getTime() : 0;
        // Youngest first when asc (largest DOB timestamp)
        return (bTime - aTime) * dir;
      }
      if (sortField === 'last_visit') {
        const aLv = a.last_visit ? new Date(a.last_visit).getTime() : 0;
        const bLv = b.last_visit ? new Date(b.last_visit).getTime() : 0;
        return (aLv - bLv) * dir;
      }
      const getVal = (p: Patient): string => {
        switch (sortField) {
          case 'name': return p.name || '';
          case 'species': return p.species || '';
          case 'breed': return p.breed || '';
          case 'gender': return p.gender || '';
          case 'client_name': return p.client_name || '';
          case 'status': return p.status || '';
          default: return '';
        }
      };
      return getVal(a).localeCompare(getVal(b)) * dir;
    });
  }, [patients, search, filterSpecies, filterStatus, filterSex, sortField, sortDir]);

  const paginatedRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const pageIds = paginatedRows.map(p => p.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const somePageSelected = pageIds.some(id => selected.has(id)) && !allPageSelected;

  // ── Sort handler ──────────────────────────────────────────────────────────

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  };

  const sortProps = (field: SortField) => ({
    active: sortField === field,
    direction: sortField === field ? sortDir : 'asc' as const,
    onClick: () => handleSort(field),
  });

  // ── Selection ─────────────────────────────────────────────────────────────

  const toggleSelectAll = () => {
    const next = new Set(selected);
    if (allPageSelected) pageIds.forEach(id => next.delete(id));
    else pageIds.forEach(id => next.add(id));
    setSelected(next);
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  // ── Archive ───────────────────────────────────────────────────────────────

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    setArchiveError('');
    try {
      await api.put(API_ENDPOINTS.PATIENTS.UPDATE(archiveTarget.id), { status: 'inactive' });
      await fetchPatients();
      setArchiveTarget(null);
    } catch {
      setArchiveError('Failed to archive patient. Please try again.');
    } finally {
      setArchiving(false);
    }
  };

  // ── New Patient dialog ────────────────────────────────────────────────────

  const handleOpenDialog = () => {
    setForm(emptyForm);
    setSelectedClient(null);
    setFormError(null);
    setMicrochipError(null);
    setFormSuccess(false);
    setBreedList([]);
    setDialogOpen(true);
    fetchReferenceData();
  };

  // ISSUE-004: Check microchip uniqueness on blur
  const checkMicrochip = async (microchipId: string) => {
    if (!microchipId?.trim()) {
      setMicrochipError(null);
      return;
    }
    setCheckingMicrochip(true);
    try {
      const response = await api.get(`/patients/check-microchip/${encodeURIComponent(microchipId.trim())}`);
      if (response.data?.exists) {
        const pet = response.data.pet;
        setMicrochipError(`Microchip already registered to ${pet.name} (Owner: ${pet.owner_name})`);
      } else {
        setMicrochipError(null);
      }
    } catch (err) {
      console.error('Failed to check microchip:', err);
    } finally {
      setCheckingMicrochip(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !selectedClient) {
      setFormError('Patient name and owner are required.');
      return;
    }
    if (microchipError) {
      setFormError('Please fix the microchip error before submitting.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await api.post(API_ENDPOINTS.PATIENTS.CREATE, {
        client_id: selectedClient.id,
        name: form.name.trim(),
        species_id: form.species_id ? Number(form.species_id) : undefined,
        breed_id: form.breed_id ? Number(form.breed_id) : undefined,
        date_of_birth: form.date_of_birth || undefined,
        gender: form.gender || undefined,
        weight: form.weight ? Number(form.weight) : undefined,
        microchip_id: form.microchip_id.trim() || undefined,
        color: form.color.trim() || undefined,
        allergies: form.allergies.trim() || undefined,
        chronic_illnesses: form.chronic_illnesses.trim() || undefined,
      });
      setFormSuccess(true);
      await fetchPatients();
      setTimeout(() => setDialogOpen(false), 800);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setFormError(msg || 'Failed to create patient.');
    } finally {
      setSaving(false);
    }
  };

  // ── CSV export for selected/all ───────────────────────────────────────────

  const handleExport = () => {
    const rows = selected.size > 0
      ? filtered.filter(p => selected.has(p.id))
      : filtered;
    exportToCSV(rows);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Patients</Typography>
        <Tooltip title="Register new patient (link to owner if existing)">
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
            New Patient
          </Button>
        </Tooltip>
      </Box>

      {/* Stats Bar */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Patients', value: stats.total, color: 'text.primary' },
          { label: 'Active', value: stats.active, color: 'success.main' },
          { label: 'New This Month', value: stats.newThisMonth, color: 'primary.main' },
          { label: 'Average Age', value: stats.avgAge, color: 'text.secondary' },
        ].map(({ label, value, color }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Card variant="outlined" sx={{ textAlign: 'center', py: 1 }}>
              <CardContent sx={{ py: '12px !important' }}>
                <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by name, species, breed, owner, microchip..."
        value={search}
        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Species</InputLabel>
          <Select
            multiple
            value={filterSpecies}
            label="Species"
            onChange={(e: SelectChangeEvent<string[]>) => {
              setFilterSpecies(e.target.value as string[]);
              setPage(0);
            }}
            renderValue={(selected) => (selected as string[]).join(', ')}
          >
            {uniqueSpecies.map(s => (
              <MenuItem key={s} value={s}>
                <Checkbox checked={filterSpecies.includes(s)} size="small" />
                {getSpeciesEmoji(s)} {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e: SelectChangeEvent) => { setFilterStatus(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="deceased">Deceased</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Sex</InputLabel>
          <Select
            value={filterSex}
            label="Sex"
            onChange={(e: SelectChangeEvent) => { setFilterSex(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="male">Male ♂</MenuItem>
            <MenuItem value="female">Female ♀</MenuItem>
            <MenuItem value="neutered">Neutered ♂</MenuItem>
            <MenuItem value="spayed">Spayed ♀</MenuItem>
          </Select>
        </FormControl>

        {(filterSpecies.length > 0 || filterStatus || filterSex || search) && (
          <Button
            size="small"
            variant="text"
            onClick={() => { setFilterSpecies([]); setFilterStatus(''); setFilterSex(''); setSearch(''); setPage(0); }}
          >
            Clear Filters
          </Button>
        )}
      </Stack>

      {/* Bulk toolbar */}
      {selected.size > 0 && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'primary.50' }}
        >
          <Typography variant="body2" fontWeight={600}>
            {selected.size} patient{selected.size !== 1 ? 's' : ''} selected
          </Typography>
          <Button size="small" startIcon={<ExportIcon />} onClick={handleExport}>
            Export CSV
          </Button>
          <Button size="small" color="warning"
            onClick={() => {
              const targets = filtered.filter(p => selected.has(p.id) && p.status === 'active');
              if (targets.length > 0) setArchiveTarget(targets[0]);
            }}
          >
            Mark Inactive
          </Button>
          <Button size="small" onClick={() => setSelected(new Set())}>
            Deselect All
          </Button>
        </Paper>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ width: 52 }} />
              <TableCell>
                <TableSortLabel {...sortProps('name')}>
                  <strong>Name</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel {...sortProps('species')}>Species</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel {...sortProps('breed')}>Breed</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel {...sortProps('gender')}>Sex</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel {...sortProps('age')}>Age</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel {...sortProps('client_name')}>Owner</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel {...sortProps('last_visit')}>Last Visit</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel {...sortProps('status')}>Status</TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <PetsIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
                    <Typography variant="h6" color="text.secondary">No patients found</Typography>
                    <Typography variant="body2" color="text.disabled">
                      {search || filterSpecies.length > 0 || filterStatus || filterSex
                        ? 'Try adjusting your search or filters'
                        : 'Add your first furry friend!'}
                    </Typography>
                    {!search && !filterSpecies.length && !filterStatus && !filterSex && (
                      <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog} sx={{ mt: 1 }}>
                        New Patient
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map(patient => {
                const old = isOldVisit(patient.last_visit);
                return (
                  <Tooltip
                    key={patient.id}
                    title={patient.microchip_id ? `Microchip: ${patient.microchip_id}` : ''}
                    placement="left"
                    arrow
                  >
                    <TableRow
                      hover
                      selected={selected.has(patient.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selected.has(patient.id)}
                          onChange={() => toggleSelect(patient.id)}
                        />
                      </TableCell>

                      {/* Avatar */}
                      <TableCell onClick={() => navigate(`/patients/${patient.id}`)}>
                        <Avatar
                          sx={{
                            width: 36, height: 36, fontSize: 20,
                            bgcolor: 'grey.100',
                          }}
                        >
                          {getSpeciesEmoji(patient.species)}
                        </Avatar>
                      </TableCell>

                      {/* Name */}
                      <TableCell onClick={() => navigate(`/patients/${patient.id}`)}>
                        <Typography fontWeight={700} variant="body2">
                          {patient.name}
                        </Typography>
                      </TableCell>

                      {/* Species */}
                      <TableCell onClick={() => navigate(`/patients/${patient.id}`)}>
                        <Chip
                          label={patient.species || '—'}
                          size="small"
                          color={getSpeciesColor(patient.species)}
                          variant="outlined"
                          sx={{ fontSize: 11 }}
                        />
                      </TableCell>

                      {/* Breed */}
                      <TableCell onClick={() => navigate(`/patients/${patient.id}`)}>
                        <Typography variant="body2" color="text.secondary">
                          {patient.breed || '—'}
                        </Typography>
                      </TableCell>

                      {/* Sex */}
                      <TableCell onClick={() => navigate(`/patients/${patient.id}`)}>
                        <Typography variant="body2">{getSexLabel(patient.gender)}</Typography>
                      </TableCell>

                      {/* Age */}
                      <TableCell onClick={() => navigate(`/patients/${patient.id}`)}>
                        <Tooltip
                          title={patient.date_of_birth ? `Born ${fmtDate(patient.date_of_birth)}` : ''}
                          arrow
                        >
                          <Typography variant="body2">
                            {formatAge(patient.date_of_birth)}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* Owner */}
                      <TableCell>
                        {patient.client_name ? (
                          <Button
                            size="small"
                            variant="text"
                            sx={{ p: 0, minWidth: 0, textAlign: 'left', fontSize: 12 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/clients/${patient.client_id}`);
                            }}
                          >
                            {patient.client_name}
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Last Visit */}
                      <TableCell onClick={() => navigate(`/patients/${patient.id}`)}>
                        <Typography variant="body2" color={old ? 'text.disabled' : 'text.primary'}>
                          {fmtDate(patient.last_visit)}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell onClick={() => navigate(`/patients/${patient.id}`)}>
                        <Chip
                          label={patient.status}
                          size="small"
                          color={getStatusColor(patient.status)}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Stack direction="row" spacing={0} justifyContent="flex-end">
                          <Tooltip title="View patient" arrow>
                            <IconButton size="small" onClick={() => navigate(`/patients/${patient.id}`)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit patient" arrow>
                            <IconButton size="small" onClick={() => navigate(`/patients/${patient.id}`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Archive patient (medical history preserved)" arrow>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={(e) => { e.stopPropagation(); setArchiveTarget(patient); setArchiveError(''); }}
                              disabled={patient.status !== 'active'}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </Tooltip>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
        <Button
          size="small"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          disabled={filtered.length === 0}
        >
          Export {selected.size > 0 ? `${selected.size} selected` : 'all'}
        </Button>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          labelDisplayedRows={({ from, to, count }) => `Showing ${from}–${to} of ${count} patients`}
        />
      </Box>

      {/* Archive Confirm Dialog */}
      <Dialog open={!!archiveTarget} onClose={() => setArchiveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Archive Patient?</DialogTitle>
        <DialogContent>
          {archiveError && <Alert severity="error" sx={{ mb: 2 }}>{archiveError}</Alert>}
          <Typography>
            Archive <strong>{archiveTarget?.name}</strong>? The patient will be marked as inactive
            but all medical history will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)} disabled={archiving}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleArchive} disabled={archiving}>
            {archiving ? <CircularProgress size={20} /> : 'Archive Patient'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Patient Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Patient</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          {formSuccess && <Alert severity="success" sx={{ mb: 2 }}>Patient created successfully.</Alert>}
          <Stack spacing={2} mt={1}>
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(o) => o.label}
              value={selectedClient}
              onChange={(_, val) => setSelectedClient(val)}
              renderInput={(params) => <TextField {...params} label="Owner (Client) *" size="small" />}
            />
            <TextField
              label="Patient Name *"
              fullWidth size="small"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            />
            <Box display="flex" gap={1}>
              <TextField
                select label="Species" fullWidth size="small"
                value={form.species_id}
                onChange={(e) => {
                  setForm(f => ({ ...f, species_id: e.target.value, breed_id: '' }));
                  fetchBreeds(e.target.value);
                }}
              >
                <MenuItem value="">— Select Species —</MenuItem>
                {speciesList.map(s => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {getSpeciesEmoji(s.name)} {s.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select label="Breed" fullWidth size="small"
                value={form.breed_id}
                onChange={(e) => setForm(f => ({ ...f, breed_id: e.target.value }))}
                disabled={!form.species_id || breedList.length === 0}
              >
                <MenuItem value="">— Unknown / Mixed —</MenuItem>
                {breedList.map(b => (
                  <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box display="flex" gap={1}>
              <TextField
                label="Date of Birth" type="date" fullWidth size="small"
                InputLabelProps={{ shrink: true }}
                value={form.date_of_birth}
                onChange={(e) => setForm(f => ({ ...f, date_of_birth: e.target.value }))}
                inputProps={{ max: new Date().toISOString().slice(0, 10) }}
              />
              <TextField
                select label="Sex" fullWidth size="small"
                value={form.gender}
                onChange={(e) => setForm(f => ({ ...f, gender: e.target.value }))}
              >
                <MenuItem value="Unknown">Unknown</MenuItem>
                <MenuItem value="Male">Male ♂</MenuItem>
                <MenuItem value="Female">Female ♀</MenuItem>
                <MenuItem value="Male (neutered)">Male (neutered) ♂✂</MenuItem>
                <MenuItem value="Female (spayed)">Female (spayed) ♀✂</MenuItem>
              </TextField>
            </Box>
            <Box display="flex" gap={1}>
              <TextField
                label="Weight (kg)" type="number" fullWidth size="small"
                value={form.weight}
                onChange={(e) => setForm(f => ({ ...f, weight: e.target.value }))}
                inputProps={{ min: 0, step: 0.1 }}
              />
              <TextField
                label="Color / Markings" fullWidth size="small"
                value={form.color}
                onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))}
              />
            </Box>
            <TextField
              label="Microchip ID" fullWidth size="small"
              value={form.microchip_id}
              onChange={(e) => setForm(f => ({ ...f, microchip_id: e.target.value }))}
              onBlur={(e) => checkMicrochip(e.target.value)}
              error={!!microchipError}
              helperText={microchipError || (checkingMicrochip ? 'Checking...' : '')}
              disabled={checkingMicrochip}
            />
            <TextField
              label="Known Allergies" fullWidth size="small" multiline rows={2}
              placeholder="e.g. Penicillin, flea treatment..."
              value={form.allergies}
              onChange={(e) => setForm(f => ({ ...f, allergies: e.target.value }))}
            />
            <TextField
              label="Chronic Illnesses / Conditions" fullWidth size="small" multiline rows={2}
              placeholder="e.g. Diabetes, hypothyroidism..."
              value={form.chronic_illnesses}
              onChange={(e) => setForm(f => ({ ...f, chronic_illnesses: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Create Patient'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientsPage;
